import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { getApiErrorMessage } from '#/shared/api/api-error'
import { httpClient } from '#/shared/api/http-client'
import {
  ADMIN_AUTH_PASSKEY_OPTIONS_PATH,
  ADMIN_AUTH_PASSKEY_PATH,
  ADMIN_AUTH_PASSKEY_SETUP_PATH,
  ADMIN_AUTH_RECOVERY_PATH,
  ADMIN_PASSKEYS_PATH,
  ADMIN_SUPERUSERS_PATH,
} from '#/shared/api/paths'

import { authKeys, type SetupRequiredResponse } from './api'
import {
  clearPendingSetup,
  readPendingSetup,
  storePendingSetup,
  storeSession,
  type AdminUser,
} from './session'

export interface Passkey {
  id: string
  name: string
  device_type: 'single_device' | 'multi_device'
  backed_up: boolean
  transports: string[]
  aaguid: string
  created_at: string
  last_used_at: string | null
}

export interface PasskeySignInResponse {
  access_token: string
  refresh_token: string
  user: AdminUser
}

interface SetupFinishedResponse extends PasskeySignInResponse {
  recovery_codes: string[] | null
}

export const passkeyKeys = {
  mine: ['auth', 'passkeys'] as const,
  recoveryCodes: ['auth', 'passkeys', 'recovery-codes'] as const,
  ofAdmin: (adminId: string) => ['admins', adminId, 'passkeys'] as const,
}

export function apiErrorCode(error: unknown): string | null {
  if (!isAxiosError<{ code?: unknown }>(error)) return null
  const code = error.response?.data?.code
  return typeof code === 'string' ? code : null
}

export function passkeyDeviceLabel(passkey: Passkey): {
  text: string
  variant: 'success' | 'neutral'
} {
  if (passkey.device_type === 'multi_device') {
    return {
      text: passkey.backed_up ? 'synced' : 'not synced yet',
      variant: 'success',
    }
  }
  return { text: 'this device only', variant: 'neutral' }
}

export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn()
}

/**
 * Closing the browser's passkey sheet is a choice, not a failure.
 *
 * The browser reports it as `NotAllowedError` — the same error it uses for a
 * timeout — so both read as "nothing happened" rather than an alarm.
 */
export function passkeyCancelled(error: unknown): boolean {
  if (error instanceof WebAuthnError && error.code === 'ERROR_CEREMONY_ABORTED') {
    return true
  }
  const cause = error instanceof WebAuthnError ? error.cause : error
  return cause instanceof Error && cause.name === 'NotAllowedError'
}

const WEBAUTHN_MESSAGES: Partial<Record<string, string>> = {
  ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED: 'This passkey is already added to your account.',
  ERROR_INVALID_DOMAIN: 'Passkeys do not work on this address of the console.',
  ERROR_INVALID_RP_ID: 'Passkeys do not work on this address of the console.',
}

const CODE_MESSAGES: Partial<Record<string, string>> = {
  PASSKEY_SETUP_EXPIRED: 'Adding the passkey took too long. Sign in again to start over.',
  LAST_PASSKEY: 'This is your only passkey. Add another one before removing it.',
}

const STATUS_MESSAGES: Partial<Record<number, string>> = {
  401: 'That passkey could not sign you in. Use another one or your password.',
  503: 'Passkeys are not set up for this console yet.',
}

/**
 * What to say when a passkey ceremony goes wrong.
 *
 * The API answers every refused sign-in with one 401 on purpose, so it never
 * says which passkeys exist; the console turns that into the one thing the
 * person can act on.
 */
export function passkeyErrorMessage(error: unknown): string {
  if (error instanceof WebAuthnError) return WEBAUTHN_MESSAGES[error.code] ?? error.message
  const status = isAxiosError(error) ? error.response?.status : undefined
  return (
    CODE_MESSAGES[apiErrorCode(error) ?? ''] ??
    STATUS_MESSAGES[status ?? 0] ??
    getApiErrorMessage(error)
  )
}

export interface PasskeyChallenge {
  ceremony_id: string
  options: PublicKeyCredentialRequestOptionsJSON
  expires_in: number
}

export async function requestPasskeyChallenge(): Promise<PasskeyChallenge> {
  const { data } = await httpClient.post<PasskeyChallenge>(ADMIN_AUTH_PASSKEY_OPTIONS_PATH)
  return data
}

export async function exchangePasskey(
  challenge: PasskeyChallenge,
  credential: AuthenticationResponseJSON,
): Promise<PasskeySignInResponse> {
  const { data } = await httpClient.post<PasskeySignInResponse>(ADMIN_AUTH_PASSKEY_PATH, {
    ceremony_id: challenge.ceremony_id,
    credential,
  })
  return data
}

export function openPasskeySession(
  queryClient: QueryClient,
  data: PasskeySignInResponse,
  remember: boolean,
): void {
  storeSession(data.access_token, data.refresh_token, { remember })
  queryClient.setQueryData(authKeys.me, data.user)
}

export function usePasskeySignIn() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ remember }: { remember: boolean }) => {
      const challenge = await requestPasskeyChallenge()
      const credential = await startAuthentication({ optionsJSON: challenge.options })
      return { data: await exchangePasskey(challenge, credential), remember }
    },
    onSuccess: ({ data, remember }) => openPasskeySession(queryClient, data, remember),
  })
}

export function usePasskeys() {
  return useQuery({
    queryKey: passkeyKeys.mine,
    queryFn: async (): Promise<Passkey[]> => {
      const { data } = await httpClient.get<{ results: Passkey[] }>(ADMIN_PASSKEYS_PATH)
      return data.results
    },
  })
}

export function useAddPasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const { data: start } = await httpClient.post<{
        options: PublicKeyCredentialCreationOptionsJSON
      }>(`${ADMIN_PASSKEYS_PATH}/options`)
      const credential = await startRegistration({ optionsJSON: start.options })
      const { data } = await httpClient.post<Passkey>(ADMIN_PASSKEYS_PATH, {
        credential,
        name: name.trim() || null,
      })
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passkeyKeys.mine }),
  })
}

export function useRenamePasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await httpClient.patch<Passkey>(`${ADMIN_PASSKEYS_PATH}/${id}`, { name })
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passkeyKeys.mine }),
  })
}

export function useRemovePasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await httpClient.delete(`${ADMIN_PASSKEYS_PATH}/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passkeyKeys.mine }),
  })
}

export function useRecoverySignIn() {
  return useMutation({
    mutationFn: async (input: { email: string; code: string; remember: boolean }) => {
      const { data } = await httpClient.post<SetupRequiredResponse>(ADMIN_AUTH_RECOVERY_PATH, {
        email: input.email,
        code: input.code,
      })
      storePendingSetup({
        setupToken: data.setup_token,
        email: data.user.email,
        reason: 'recovery',
        remember: input.remember,
        expiresAt: Date.now() + data.expires_in * 1000,
      })
      return data
    },
  })
}

/**
 * Add the passkey a pending sign-in is waiting for, then open the console.
 *
 * Returns the recovery codes when the server issued new ones — the only time
 * they can be shown.
 */
export function useFinishPasskeySetup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const pending = readPendingSetup()
      if (!pending) throw new Error('This sign-in expired. Sign in again to add a passkey.')
      const { data: start } = await httpClient.post<{
        options: PublicKeyCredentialCreationOptionsJSON
      }>(`${ADMIN_AUTH_PASSKEY_SETUP_PATH}/options`, { setup_token: pending.setupToken })
      const credential = await startRegistration({ optionsJSON: start.options })
      const { data } = await httpClient.post<SetupFinishedResponse>(ADMIN_AUTH_PASSKEY_SETUP_PATH, {
        setup_token: pending.setupToken,
        credential,
        name: name.trim() || null,
      })
      storeSession(data.access_token, data.refresh_token, { remember: pending.remember })
      clearPendingSetup()
      queryClient.setQueryData(authKeys.me, data.user)
      return data.recovery_codes
    },
  })
}

export function useRecoveryCodeStatus() {
  return useQuery({
    queryKey: passkeyKeys.recoveryCodes,
    queryFn: async (): Promise<number> => {
      const { data } = await httpClient.get<{ remaining: number }>(
        `${ADMIN_PASSKEYS_PATH}/recovery-codes`,
      )
      return data.remaining
    },
  })
}

export function useNewRecoveryCodes() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<string[]> => {
      const { data } = await httpClient.post<{ codes: string[] }>(
        `${ADMIN_PASSKEYS_PATH}/recovery-codes`,
      )
      return data.codes
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passkeyKeys.recoveryCodes }),
  })
}

export function useAdminPasskeys(adminId: string) {
  return useQuery({
    queryKey: passkeyKeys.ofAdmin(adminId),
    queryFn: async (): Promise<Passkey[]> => {
      const { data } = await httpClient.get<{ results: Passkey[] }>(
        `${ADMIN_SUPERUSERS_PATH}/${adminId}/passkeys`,
      )
      return data.results
    },
  })
}

export function useResetAdminPasskeys() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ adminId }: { adminId: string }) => {
      await httpClient.post(`${ADMIN_SUPERUSERS_PATH}/${adminId}/passkeys/reset`)
    },
    onSuccess: (_data, { adminId }) =>
      queryClient.invalidateQueries({ queryKey: passkeyKeys.ofAdmin(adminId) }),
  })
}
